// ─── Layer Panel ─────────────────────────────────────────────
// Shows elements sorted by z-index (front → back).
// Click to select, ↑↓ to change layer order, padlock to lock/unlock.

import React from 'react';
import {
  ArrowUp, ArrowDown, Image as ImageIcon, Type, FileText,
  Heading, EyeOff, Lock, Unlock,
} from 'lucide-react';
import { computeLayerLabels } from '../../lib/utils/layerNaming';

const TYPE_ICONS = {
  image_card: ImageIcon,
  text_block: Type,
  text_note: FileText,
  title_block: Heading,
};

const ROLE_COLORS = {
  image: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  primaryHeadline: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  secondaryHeadline: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-300',
  bodyText: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  metaText: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  decorative: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  cta: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

export default function LayerPanel({
  sections,
  content,
  selectedElement,
  selectionSet,
  onSelect,
  onToggleSelection,
  onUpdateLayout,
  onToggleLock,
}) {
  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="layer-panel"
    >
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
        <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
          Layers
        </h3>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {sections.filter(s => s.visible !== false).map(section => {
          const sectionData = content[section.id] || {};
          const elements = sectionData._elements || section.elements || [];
          const merged = elements.map(el => ({
            ...el,
            ...(sectionData[el.id] || {}),
          }));

          // Phase 1.8 — compute role-aware display labels in one pass.
          const labels = computeLayerLabels(merged);

          // Sort by z descending (front elements on top)
          const sorted = [...merged].sort((a, b) => {
            const zA = a.layout?.mobile?.z ?? a.layout?.desktop?.z ?? 1;
            const zB = b.layout?.mobile?.z ?? b.layout?.desktop?.z ?? 1;
            return zB - zA;
          });

          if (sorted.length === 0) return null;

          return (
            <div key={section.id}>
              {/* Section header */}
              <div className="px-4 py-2 bg-gray-50/50 dark:bg-gray-800/30">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {section.id.replace(/-/g, ' ')}
                </span>
              </div>

              {/* Element layers */}
              {sorted.map((el, idx) => {
                const Icon = TYPE_ICONS[el.type] || Type;
                const role = el.intent?.role || 'bodyText';
                const colorCls = ROLE_COLORS[role] || ROLE_COLORS.bodyText;
                const z = el.layout?.mobile?.z ?? el.layout?.desktop?.z ?? 1;
                const isSelected =
                  selectedElement?.sectionId === section.id &&
                  selectedElement?.elementId === el.id;
                // Phase 1.9 — show a subtle indicator when the row is part
                // of the multi-select set (not the single primary selection).
                const isInMulti = Array.isArray(selectionSet)
                  ? selectionSet.some(
                      (s) => s.sectionId === section.id && s.elementId === el.id,
                    )
                  : false;

                return (
                  <div
                    key={el.id}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                      isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                        : isInMulti
                          ? 'bg-blue-50/40 dark:bg-blue-900/10 border-l-2 border-blue-300'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-2 border-transparent'
                    }`}
                    onClick={(e) => {
                      if (e.shiftKey && onToggleSelection) {
                        onToggleSelection(section.id, el.id);
                      } else {
                        onSelect(section.id, el.id);
                      }
                    }}
                    data-testid={`layer-item-${el.id}`}
                  >
                    {/* Type icon */}
                    <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${colorCls}`}>
                      <Icon className="w-3 h-3" />
                    </div>

                    {/* Label + z badge */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-medium truncate ${el.locked ? 'text-gray-400 dark:text-gray-500 italic' : 'text-gray-700 dark:text-gray-300'}`}>
                        {labels[el.id] || 'Layer'}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[9px] text-gray-400 dark:text-gray-500">
                          z:{z}
                        </span>
                        {el.intent?.mobileBehavior === 'hide' && (
                          <span className="text-[9px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                            <EyeOff className="w-2.5 h-2.5" /> hidden on mobile
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Lock toggle */}
                    {onToggleLock && (
                      <button
                        className={`p-1 rounded transition-colors flex-shrink-0 ${
                          el.locked
                            ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                            : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleLock(section.id, el.id, !el.locked);
                        }}
                        title={el.locked ? 'Unlock' : 'Lock'}
                        data-testid={`layer-lock-${el.id}`}
                      >
                        {el.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                      </button>
                    )}

                    {/* Z controls */}
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-30"
                        disabled={idx === 0 || el.locked}
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateLayout(section.id, el.id, 'z', Math.min(20, z + 1));
                        }}
                        title="Bring Forward"
                        data-testid={`layer-z-up-${el.id}`}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-30"
                        disabled={idx === sorted.length - 1 || el.locked}
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdateLayout(section.id, el.id, 'z', Math.max(1, z - 1));
                        }}
                        title="Send Backward"
                        data-testid={`layer-z-down-${el.id}`}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
